/**
 * Tile renderer and row tags for the supplier Documents tab. The list itself is
 * the Documents page's (document-manager/DocumentRows), so both screens show the
 * same columns and sort the same way. The folder badge links
 * into the side-menu Documents page (?folder=<id>) — the same navigation
 * contract that page uses for its own breadcrumb state.
 */
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Download, FileText, File, FileSpreadsheet, Image, FileArchive, Link, Loader2, Trash2,
} from 'lucide-react';
import type { Document as DocType } from '../../lib/api';

const IMPORTANCE_CONFIG: Record<string, { label: string; className: string }> = {
  low: { label: 'Low', className: 'bg-gray-100 text-gray-700' },
  medium: { label: 'Medium', className: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', className: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', className: 'bg-red-100 text-red-700' },
};

function getFileIcon(doc: DocType) {
  if (doc.document_type === 'url') return <Link className="h-5 w-5 text-blue-500" />;
  const ext = (doc.file_extension || '').toLowerCase();
  if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return <FileText className="h-5 w-5 text-red-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp'].includes(ext)) return <Image className="h-5 w-5 text-purple-500" />;
  if (['zip', 'rar', '7z'].includes(ext)) return <FileArchive className="h-5 w-5 text-amber-600" />;
  return <File className="h-5 w-5 text-gray-500" />;
}

function ImportanceBadge({ importance }: { importance?: string }) {
  if (!importance) return null;
  const config = IMPORTANCE_CONFIG[importance];
  if (!config) return null;
  return <Badge className={`text-[10px] px-1.5 py-0 ${config.className}`}>{config.label}</Badge>;
}

interface DocViewProps {
  docs: DocType[];
  formatDate: (d: string) => string;
  deletingId: number | null;
  onDownload: (doc: DocType) => void;
  onDelete?: (doc: DocType) => void;
  /** Jump to the side-menu Documents page opened at this folder. */
  onOpenFolder: (categoryId: number) => void;
  onOpenDetail: (doc: DocType) => void;
}

/**
 * The supplier tab's own tags on a row of the shared document list: the folder
 * (a link into that folder on the Documents page), importance and the R&U flag.
 */
export function SupplierDocBadges({ doc, onOpenFolder }: { doc: DocType; onOpenFolder: (categoryId: number) => void }) {
  return (
    <>
      {doc.category_name && (
        doc.category != null ? (
          <Badge
            variant="outline"
            className="text-[10px] py-0 h-4 cursor-pointer hover:bg-muted"
            title={`Open "${doc.category_name}" in Documents`}
            onClick={(e) => { e.stopPropagation(); onOpenFolder(doc.category as number); }}
          >
            {doc.category_name}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] py-0 h-4">{doc.category_name}</Badge>
        )
      )}
      <ImportanceBadge importance={doc.importance} />
      {doc.requires_company_confirmation && (
        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-800 hover:bg-amber-100">
          R&amp;U required
        </Badge>
      )}
    </>
  );
}

export function DocTilesView({ docs, formatDate, deletingId, onDownload, onDelete, onOpenDetail, onOpenFolder }: DocViewProps) {
  return (
    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {docs.map((doc) => (
        <Card key={doc.id} className="group relative hover:shadow-md transition-shadow cursor-pointer" onClick={() => onOpenDetail(doc)}>
          <CardContent className="pt-4 flex flex-col items-center text-center gap-2">
            <div className="w-16 h-16 flex items-center justify-center">{getFileIcon(doc)}</div>
            <p className="text-xs font-medium truncate w-full" title={doc.title}>{doc.title}</p>
            <ImportanceBadge importance={doc.importance} />
            {doc.category_name && doc.category != null && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 cursor-pointer hover:bg-muted"
                title={`Open "${doc.category_name}" in Documents`}
                onClick={(e) => { e.stopPropagation(); onOpenFolder(doc.category as number); }}
              >
                {doc.category_name}
              </Badge>
            )}
            <p className="text-[10px] text-muted-foreground">{doc.formatted_file_size || ''}</p>
            <p className="text-[10px] text-muted-foreground">{formatDate(doc.version_uploaded_at || doc.uploaded_at)}</p>
            <div className="flex gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDownload(doc)} title="Download"><Download className="h-3.5 w-3.5" /></Button>
              {onDelete && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => onDelete(doc)} disabled={deletingId === doc.id} title="Delete">
                  {deletingId === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

